/**
 * DELIVER-LEDGER — turn-scoped outbound duplicate-delivery guard.
 *
 * The disease (prod task 832d60fd, 2026-08-12): one user intent — "send the
 * image to #marketing" — executed as TWO Composio writes 8s apart (an upload
 * with initial_comment, then a SEND_MESSAGE to the same channel), because two
 * COMPOSIO_SEARCH_TOOLS plans each marked a DIFFERENT delivery verb Required
 * and the agent ran both. Every file-bearing Slack turn in the prior 30 days
 * double-delivered the same way. Composio's own pitfall text ("posting is not
 * idempotent; retries can create duplicate messages") is advisory prose the
 * model may or may not act on — this module is that rule as a GATE.
 *
 * A DELIVERY is a Composio call that puts content in front of a human at a
 * destination: its slug carries a deliver verb (whole-word, hasVerbWord) AND
 * its arguments carry an extractable destination (channel/to/recipient/...).
 * Successful deliveries are recorded per (task, turn, toolkit, destination);
 * `check` denies:
 *   R1 — the same slug re-delivering the SAME payload to a destination already
 *        delivered-ok this turn (the classic post-error retry dupe);
 *   R2 — a DIFFERENT slug of the same toolkit delivering to a destination
 *        already delivered-ok this turn (the upload-then-send dupe);
 *   lint — any delivery embedding an EXPIRING Vestra signed storage URL
 *        (`/storage/v1/object/sign/`): outbound messages are permanent, the
 *        link dies in days (the incident's #marketing image breaks ~09-11).
 *
 * Guard posture: fail-OPEN at extract time (an unreadable entry is skipped —
 * a deny false-positive breaks a legitimate task), fail-CLOSED at record time
 * (an unreadable RESULT records the delivery — an errored post may still have
 * landed, and a blocked retry is recoverable while a double-send is not).
 * Same-slug/different-payload to the same destination stays ALLOWED ("post 3
 * updates to #general" is one intent, three deliveries). Turn scope is
 * load-bearing: "send it again" next turn must work — the product bumps the
 * turn at the turn-dispatch seam (interaction resumes deliberately do NOT
 * bump, so a pre-pause delivery stays guarded after resume).
 *
 * PURE policy + in-memory state; no I/O. The product owns the instance and
 * keys every entry by taskId (multi-session sandbox rule).
 */

import type { ToolCall } from '../contract/harness.js';
import { COMPOSIO_TOOL_PREFIX } from './engine-constants.js';
import { isComposioExecutorMetaTool, isComposioMetaTool } from './composio-naming.js';
import { hasVerbWord } from './sensitivity-floor.js';

/** Slug words that mark a call as PUTTING CONTENT IN FRONT OF A HUMAN. UPLOAD/
 *  FILE alone are not enough — the destination requirement below keeps a
 *  Drive/folder upload (no channel/recipient argument) unguarded. */
export const DELIVER_VERBS: readonly string[] = [
  'SEND', 'REPLY', 'POST', 'PUBLISH', 'COMMENT', 'MESSAGE', 'ANNOUNCE', 'TWEET',
  'UPLOAD', 'FILE',
];

/** Argument fields that name the audience. Deliberately messaging-shaped —
 *  `folder_id`/`parent_id` style container fields are NOT destinations. */
const DESTINATION_FIELDS: readonly string[] = [
  'channel', 'channels', 'channel_id', 'conversation_id',
  'to', 'recipient', 'recipients', 'chat_id',
];

export interface DeliveryDescriptor {
  /** Uppercased Composio tool slug, e.g. `SLACK_SEND_MESSAGE`. */
  slug: string;
  /** Lowercased toolkit prefix of the slug, e.g. `slack`. */
  toolkit: string;
  /** Normalized (lowercased, trimmed) destination identifiers. */
  destinations: string[];
  /** Stable fingerprint of the arguments, for the R1 identical-retry rule. */
  argsKey: string;
  /** The raw arguments object (consulted by the signed-URL lint). */
  args: unknown;
}

/** Stable stringify (sorted keys, depth-unlimited) so two payloads that differ
 *  only in key order fingerprint identically. Never throws. */
function stableKey(value: unknown): string {
  const seen = new Set<unknown>();
  const walk = (v: unknown): unknown => {
    if (v === null || typeof v !== 'object') return v;
    if (seen.has(v)) return '[circular]';
    seen.add(v);
    if (Array.isArray(v)) return v.map(walk);
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(v as Record<string, unknown>).sort()) {
      out[k] = walk((v as Record<string, unknown>)[k]);
    }
    return out;
  };
  try {
    return JSON.stringify(walk(value));
  } catch {
    return String(value);
  }
}

function normalizeDest(v: unknown): string[] {
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    return s === '' ? [] : [s];
  }
  if (Array.isArray(v)) return v.flatMap(normalizeDest);
  return [];
}

function destinationsOf(args: unknown): string[] {
  if (!args || typeof args !== 'object') return [];
  const out: string[] = [];
  for (const f of DESTINATION_FIELDS) {
    out.push(...normalizeDest((args as Record<string, unknown>)[f]));
  }
  return [...new Set(out)];
}

function toDescriptor(rawSlug: string, args: unknown): DeliveryDescriptor | null {
  const slug = rawSlug.toUpperCase();
  if (!hasVerbWord(slug, DELIVER_VERBS)) return null;
  const destinations = destinationsOf(args);
  if (destinations.length === 0) return null;
  const underscore = slug.indexOf('_');
  const toolkit = (underscore > 0 ? slug.slice(0, underscore) : slug).toLowerCase();
  return { slug, toolkit, destinations, argsKey: stableKey(args), args };
}

/**
 * Extract the DELIVERIES a tool call would perform. Sources: a direct
 * `mcp__composio__<SLUG>` call (non-meta), and the executor meta-tool's inner
 * `tools[] = { tool_slug, arguments }` entries. Unreadable entries are SKIPPED
 * (fail-open) — this guard denies only what it can read clearly.
 */
export function extractDeliveries(call: ToolCall): DeliveryDescriptor[] {
  try {
    if (typeof call?.toolName !== 'string') return [];
    if (isComposioExecutorMetaTool(call.toolName)) {
      const tools = (call.toolInput as { tools?: unknown } | null)?.tools;
      if (!Array.isArray(tools)) return [];
      const out: DeliveryDescriptor[] = [];
      for (const entry of tools) {
        const slug = (entry as { tool_slug?: unknown } | null)?.tool_slug;
        if (typeof slug !== 'string' || slug === '') continue;
        const d = toDescriptor(slug, (entry as { arguments?: unknown }).arguments);
        if (d) out.push(d);
      }
      return out;
    }
    if (call.toolName.startsWith(COMPOSIO_TOOL_PREFIX) && !isComposioMetaTool(call.toolName)) {
      const d = toDescriptor(call.toolName.slice(COMPOSIO_TOOL_PREFIX.length), call.toolInput);
      return d ? [d] : [];
    }
    return [];
  } catch {
    return [];
  }
}

/** An EXPIRING Vestra signed storage URL marker. `/object/public/` never matches. */
const SIGNED_URL_RE = /https?:\/\/[^\s"'`]*\/storage\/v1\/object\/sign\/[^\s"'`]*/;

/** First expiring signed storage URL anywhere in a value, or null. Depth-capped. */
export function findExpiringSignedUrl(value: unknown, depth = 0): string | null {
  if (depth > 8 || value == null) return null;
  if (typeof value === 'string') return value.match(SIGNED_URL_RE)?.[0] ?? null;
  if (Array.isArray(value)) {
    for (const v of value) {
      const hit = findExpiringSignedUrl(v, depth + 1);
      if (hit) return hit;
    }
    return null;
  }
  if (typeof value === 'object') {
    for (const v of Object.values(value as Record<string, unknown>)) {
      const hit = findExpiringSignedUrl(v, depth + 1);
      if (hit) return hit;
    }
  }
  return null;
}

interface DeliveredEntry {
  slug: string;
  argsKey: string;
}

interface TaskState {
  /** `${toolkit} ${destination}` → successful deliveries this turn. */
  byDest: Map<string, DeliveredEntry[]>;
}

const destKey = (d: DeliveryDescriptor, dest: string): string => `${d.toolkit} ${dest}`;

/**
 * Tolerant per-inner-slug success reader for a Composio result body. Returns a
 * predicate `ok(slug)`. Readable `response.successful === false` for a slug ⇒
 * that slug's delivery is NOT recorded (retry stays allowed). Anything
 * unreadable ⇒ `true` (record — fail toward no-duplicate).
 */
function successReader(resultText: string | undefined): (slug: string) => boolean {
  if (resultText === undefined) return () => true;
  try {
    let v: unknown = JSON.parse(resultText);
    // Unwrap the MCP content-block envelope: [{ type:'text', text:'{...}' }]
    if (Array.isArray(v) && typeof (v[0] as { text?: unknown } | undefined)?.text === 'string') {
      v = JSON.parse((v[0] as { text: string }).text);
    }
    const results = (v as { data?: { results?: unknown } } | null)?.data?.results;
    if (!Array.isArray(results)) {
      // Direct (non-meta) tool result: honor an explicit top-level failure.
      const successful = (v as { successful?: unknown } | null)?.successful;
      return successful === false ? () => false : () => true;
    }
    const failed = new Set<string>();
    for (const r of results) {
      const slug = (r as { tool_slug?: unknown } | null)?.tool_slug;
      const ok = (r as { response?: { successful?: unknown } } | null)?.response?.successful;
      if (typeof slug === 'string' && ok === false) failed.add(slug.toUpperCase());
    }
    return (slug: string) => !failed.has(slug.toUpperCase());
  } catch {
    return () => true;
  }
}

export class DeliverLedger {
  private tasks = new Map<string, TaskState>();

  /**
   * Would this call double-deliver? Returns the model-visible deny message, or
   * null to allow. Also runs the signed-URL lint on every delivery. Evaluates
   * the call's own deliveries SEQUENTIALLY so a single batched [upload, send]
   * to one destination is caught intra-call.
   */
  check(taskId: string, call: ToolCall): string | null {
    const deliveries = extractDeliveries(call);
    if (deliveries.length === 0) return null;

    const state = this.tasks.get(taskId);
    // Simulated view: ledger entries + earlier deliveries of THIS call.
    const pending = new Map<string, DeliveredEntry[]>();
    for (const d of deliveries) {
      const signed = findExpiringSignedUrl(d.args);
      if (signed) {
        return (
          `Blocked: this ${d.slug} delivery embeds a temporary signed URL (${signed.slice(0, 80)}…). ` +
          `The recipients keep their copy, so they would see a broken link later. Send the file's ` +
          `Vestra link (/v/<file id>) instead: the \`link\` field that present_output, studio save ` +
          `and media results return, or the one GET $VESTRA_SERVER_URL/api/box/files/<file id>?as=pass ` +
          `returns; never build one by hand. Or upload the file bytes with the toolkit's file-upload ` +
          `tool. If the recipient cannot open the link, tell the user they may need to share it ` +
          `from Vestra; never change a file's sharing yourself.`
        );
      }
      for (const dest of d.destinations) {
        const key = destKey(d, dest);
        const prior = [...(state?.byDest.get(key) ?? []), ...(pending.get(key) ?? [])];
        const identical = prior.find((p) => p.slug === d.slug && p.argsKey === d.argsKey);
        if (identical) {
          return (
            `Blocked duplicate delivery: ${d.slug} already delivered this exact payload to ` +
            `"${dest}" this turn. Do not re-send — the first delivery succeeded; report it to the ` +
            `user (with its link if available) instead of retrying.`
          );
        }
        const crossVerb = prior.find((p) => p.slug !== d.slug);
        if (crossVerb) {
          return (
            `Blocked duplicate delivery: "${dest}" already received a successful ${crossVerb.slug} ` +
            `this turn. Sending again via ${d.slug} would put the same content in front of the same ` +
            `audience twice (the upload-then-send double-post). The delivery is DONE — report it to ` +
            `the user. If they explicitly want an additional separate message, they can ask for it.`
          );
        }
        pending.set(key, [...(pending.get(key) ?? []), { slug: d.slug, argsKey: d.argsKey }]);
      }
    }
    return null;
  }

  /**
   * Record the call's deliveries after execution. `resultText` is the tool's
   * raw result body; a readable per-slug `successful === false` skips that
   * delivery (retry stays allowed), anything unreadable records (fail-closed).
   */
  record(taskId: string, call: ToolCall, resultText?: string): void {
    const deliveries = extractDeliveries(call);
    if (deliveries.length === 0) return;
    const ok = successReader(resultText);
    let state = this.tasks.get(taskId);
    for (const d of deliveries) {
      if (!ok(d.slug)) continue;
      if (!state) {
        state = { byDest: new Map() };
        this.tasks.set(taskId, state);
      }
      for (const dest of d.destinations) {
        const key = destKey(d, dest);
        const list = state.byDest.get(key) ?? [];
        list.push({ slug: d.slug, argsKey: d.argsKey });
        state.byDest.set(key, list);
      }
    }
  }

  /** New turn for this task: prior deliveries no longer guard ("send it again"). */
  bumpTurn(taskId: string): void {
    this.tasks.delete(taskId);
  }

  /** Task teardown. */
  clearTask(taskId: string): void {
    this.tasks.delete(taskId);
  }
}
